import type { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

import { semanticTypes } from 'Automations/nodeDefinitions/semanticTypes'
import type { TerminalTaskMatcher, TerminalType } from './simulation.types'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

/**
 * The terminal nodes a quote approval can reach, and their associated
 * terminal type.
 */
export const QUOTE_APPROVAL_TERMINAL_MATCHERS: TerminalTaskMatcher[] = [
  { terminalType: 'END', taskType: 'TERMINATE' },
  {
    terminalType: 'REVIEW',
    taskType: 'SIMPLE',
    taskName: 'manual_task',
    executor: TASK_EXECUTORS.reviewQuote
  },
  {
    terminalType: 'PUBLISH',
    taskType: 'SIMPLE',
    taskName: 'update_sequence_data',
    executor: TASK_EXECUTORS.publishQuote
  }
]

/**
 * Most-restrictive-first. When a fork has two terminal tasks within it, we'll
 * list the most 'aggy' outcome that will resolve. In reality, this should end
 * up being validated away by the editor, but good to account for it here regardless.
 *
 *   END (auto-reject) > REVIEW (needs a human) > PUBLISH (goes straight out)
 */
export const TERMINAL_PRECEDENCE: TerminalType[] = ['END', 'REVIEW', 'PUBLISH']

/**
 * The task names we're able to simulate. Just the one for now - if the poller
 * ever gets split into several tasks, the rest go in here too.
 */
export const SIMULATABLE_TASK_NAMES = ['get_sequence_data']

/**
 * The most routes we'll work through before giving up. A broken workflow can
 * keep branching without ever reaching an end, so without a limit we'd never
 * stop looking.
 */
export const MAX_PATHS = 256

/**
 * Defaults for quote approval workflows! A `comment` is only written in the
 * publish modal, so we simulate as if none was, which pushes a borderline quote
 * towards "needs review" rather than towards publishing on its own.
 * `signatureConfiguration` has no sensible stand-in, so it's left out entirely
 * and any rule reading it goes unresolved.
 */
export const SIMULATION_INPUT_DEFAULTS: Partial<
  Record<Dashboardv99990101Api.Automations.TriggerType, Record<string, unknown>>
> = {
  QUOTE_REQUIRES_APPROVAL: { comment: '' }
}

/**
 * Everything a quote decides. Saving the quote changes all of it, so these are
 * the values we go and fetch again once an edit has been written down.
 *
 * The pricing comparison is in here because it's worked out from the quote, so
 * it moves whenever the quote does. The last approval is in here for the same
 * reason from the other direction: saving is what sends a quote for approval,
 * so the approval it is measured against can move under us while the editor
 * stays open. Leaving a value out only means we fetch it more often than we
 * need to.
 */
export const QUOTE_SEMANTIC_TYPES: string[] = [
  ...Object.values(semanticTypes.sequence.quote),
  ...Object.values(semanticTypes.sequence.quotePricingComparison),
  ...Object.values(semanticTypes.sequence.previousQuoteApproval)
]

/**
 * Everything else a quote approval reads - the template, the customer, the
 * person asking. None of it belongs to the quote being edited, so a few minutes
 * old is good enough and we'll take it from the cache.
 */
export const NON_QUOTE_SEMANTIC_TYPES: string[] = [
  ...Object.values(semanticTypes.sequence.quoteTemplate),
  ...Object.values(semanticTypes.sequence.customer),
  ...Object.values(semanticTypes.sequence.quoteApproval),
  semanticTypes.sequence.user,
  semanticTypes.sequence.requestorId
]

/**
 * Executors we let answer with nothing. A quote that has never been approved
 * has no previous approval, so the fetch comes back empty and every reference
 * into it goes unresolved - which would otherwise cause the simulation to abort
 * in an unresolved state.
 */
export const OPTIONAL_DEPENDENCY_EXECUTORS: string[] = [
  TASK_EXECUTORS.previousQuoteApproval
]
